import { TableBuilder, TableBuilderColumn } from 'baseui/table-semantic';
import Dinero from 'dinero.js';

import { MonthlyTableCashFlowMonth } from './gql/__generated__/cashflowDataFragments';
const formatCurrency = (value: number) =>
  Dinero({ amount: value, currency: 'USD' }).toFormat('$0,0.00');

type MonthColConfig<T extends keyof MonthlyTableCashFlowMonth> = {
  string: string;
  sortable: boolean;
  numeric?: boolean;
  display: (value: MonthlyTableCashFlowMonth[T]) => string;
};

type Config = {
  [FieldName in keyof MonthlyTableCashFlowMonth]?: MonthColConfig<FieldName>;
};

const NO_DATA = '-';

const LISTING_COLS: Config = {
  month: {
    string: 'Month',
    sortable: false,
    display: (value) => value?.toLocaleString() ?? NO_DATA,
  },
  starting_balance_cents: {
    string: 'Starting Balance',
    sortable: false,
    display: (value) => formatCurrency(value),
  },
  payments_cents: {
    string: 'Payments',
    sortable: false,
    display: (value) => formatCurrency(value),
  },
  principal_cents: {
    string: 'Principal',
    sortable: false,
    display: (value) => formatCurrency(value),
  },
  interest_cents: {
    string: 'Interest',
    sortable: false,
    numeric: true,
    display: (value) => formatCurrency(value),
  },
  servicing_cents: {
    string: 'Servicing',
    sortable: false,
    numeric: true,
    display: (value) => formatCurrency(value),
  },
  prepayments_cents: {
    string: 'Prepayments',
    sortable: false,
    numeric: true,
    display: (value) => formatCurrency(value),
  },
  defaults_cents: {
    string: 'Defaults',
    sortable: false,
    numeric: true,
    display: (value) => formatCurrency(value),
  },
  losses_cents: {
    string: 'Losses',
    sortable: false,
    numeric: true,
    display: (value) => formatCurrency(value),
  },
  ending_balance_cents: {
    string: 'Ending Balance',
    sortable: false,
    display: (value) => formatCurrency(value),
  },
  cash_flow_cents: {
    string: 'Cash Flow',
    sortable: false,
    display: (value) => formatCurrency(value),
  },
};

type Props = {
  monthlyCashFlows: MonthlyTableCashFlowMonth[];
  leftAlignAll?: boolean;
  year?: number;
  normalSize?: boolean;
  /** When > 0, a leading "Period" column shows "Actual" for rows with
   *  `month <= pastMonthCount` and "Projected" for the rest. Opt-in so callers
   *  that show pure projection aren't forced into a label they don't need. */
  pastMonthCount?: number;
};

const formatMonthWithYear = (month: number, year: number): string => {
  const monthStr = month.toString().padStart(2, '0');
  return `${monthStr}/01/${year}`;
};

const MonthlyTable = ({
  monthlyCashFlows,
  leftAlignAll = false,
  year,
  normalSize = false,
  pastMonthCount,
}: Props): JSX.Element => {
  const showPeriodColumn = pastMonthCount != null && pastMonthCount > 0;

  const periodColumn = showPeriodColumn ? (
    <TableBuilderColumn key="period" id="period" sortable={false} header="Period">
      {(row: MonthlyTableCashFlowMonth) => (row.month <= pastMonthCount! ? 'Actual' : 'Projected')}
    </TableBuilderColumn>
  ) : null;

  const tableColumns = Object.entries(LISTING_COLS).map(([key, config]) => {
    if (config == null) {
      return <></>;
    }
    return (
      <TableBuilderColumn
        key={key}
        id={key}
        numeric={config.numeric}
        header={config.string}
        sortable={config.sortable}
      >
        {(row: MonthlyTableCashFlowMonth) => {
          const value = row[key as keyof MonthlyTableCashFlowMonth];
          // Special formatting for month column when year is provided
          if (key === 'month' && year && typeof value === 'number') {
            return formatMonthWithYear(value, year);
          }
          return config.display(value);
        }}
      </TableBuilderColumn>
    );
  });

  return (
    <div>
      <TableBuilder
        data={monthlyCashFlows}
        overrides={{
          Root: {
            style: {
              height: normalSize ? 'fit-content' : '410px',
              overflowY: normalSize ? 'visible' : 'auto',
            },
          },
          TableHeadCell: {
            style: {
              color: '#868e9f',
              fontSize: normalSize ? '14px' : '12px',
              fontFamily: 'Heebo',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            },
          },
          TableBodyRow: {
            style: () => ({}),
          },
          TableBodyCell: {
            style: ({ $colIndex }) => {
              // The Period column (if present) shifts every subsequent column's
              // $colIndex by 1. Month is the primary identifier and should stay
              // left-aligned; both Period and Month get left-align.
              const monthIdx = showPeriodColumn ? 1 : 0;
              const isLeadingCol = $colIndex === 0 || (showPeriodColumn && $colIndex === monthIdx);
              return {
                color: '#f4f4fd',
                fontSize: normalSize ? '14px' : '12px',
                fontFamily: 'PT Mono',
                textAlign: leftAlignAll ? 'left' : isLeadingCol ? 'left' : 'right',
              };
            },
          },
        }}
      >
        {periodColumn}
        {tableColumns}
      </TableBuilder>
    </div>
  );
};

export default MonthlyTable;
